import { REFETCH_OPTIONS, useGetStatistics, useGetStatisticsForAll } from "../services/ContentManager";
import { useGetPublicOrganizationSettings } from "services/ContentOpenManager";
import React from "react";
import { UserContext } from "./App";
import Grid from "@mui/material/Grid";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import StatsCard from "../components/charts/StatsCard";
import NTMBubbleChart from "../components/charts/NTMBubbleChart";
import dayjs from "dayjs";
import { Box, Popover, Radio, Typography } from "@mui/material";
import DateRangeIcon from "@mui/icons-material/DateRange";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import StartIconButton from "../components/buttons/StartIconButton";
import ResponsiveCircularLoading from "../components/ResponsiveCircularLoading";
import DateIntervalModal from "../components/modals/DateIntervalModal";
import { stringToUnix, numberWithSpaces } from "../services/helper";
import NTMBarChart from "../components/charts/NTMBarChart";
import NTMLinearBar from "../components/charts/NTMLinearBar";
import { LEG_TYPE, useGetLegTypeTranslation } from "../constants/legType";
import { Pill } from "../components/SettingsPills";
import { currency } from "constants/currency";
import { AGE_ORDER, AGE_RANGES } from "../constants/ageRanges";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InfoIcon from "@mui/icons-material/Info";
import { makeStyles } from "@mui/styles";
import { organizationTypes } from "./Dashboard";



const useStyles = makeStyles((theme) => ({
    tooltip: {
        fontSize: "1em",
        [theme.breakpoints.up("lg")]: {
            fontSize: "1.05em",
        },
    },
}));

export default function Statistics({ disableDateInterval, overrideInterval, startDateUrbanPoints, endDateUrbanPoints, isOdp = false, organizationType }) {
    const { t } = useTranslation();
    const user = React.useContext(UserContext);
    const { id } = useParams();
    const styles = useStyles();
    let isAll = id === "all";
    const isTouristicOrganization = organizationType === organizationTypes.TOURISTIC;
    // Sulla pagina Open Data l'iniziativa e' quella nell'URL: altrimenti un utente
    // con un'organizzazione associata vedrebbe la propria su qualunque /open/:id.
    const oid = isOdp ? id : (user.organization || id);
    const legTypeTranslation = useGetLegTypeTranslation();

    //la vista delle statistiche prende le sessioni degli ultimi 180 giorni
    let minLimitDate = dayjs(new Date()).subtract(180, 'day').format("YYYY-MM-DD")

    //ottimizzazioni per non rifare la query
    const { settings = [], status: settingsStatus } = useGetPublicOrganizationSettings(oid, REFETCH_OPTIONS);
    const [settingsLoaded, setSettingsLoaded] = React.useState(false);
    const [startDate, setStartDate] = React.useState(startDateUrbanPoints ?? {});
    const [endDate, setEndDate] = React.useState(endDateUrbanPoints ?? {});
    const [includeUsersWalletBalance, setIncludeUsersWalletBalance] = React.useState(false);

    React.useEffect(() => {
        if (!settingsLoaded && settingsStatus === 'success') {

            let newStartDate = settings.find(s => s.organizationSetting === 'startDateUrbanPoints') || {};

            if (dayjs(newStartDate.value).diff(dayjs(minLimitDate)) < 0 && !isOdp) {
                /**
                 * la data di inizio non deve essere inferiore a quella della vista delle statistiche 
                 * Se però il componente viene reinderizzato dalla pagina delle ODP i dati sono cacheati
                 * non c'è bisogno quindi di limitare la query
                 */
                newStartDate.value = minLimitDate;
            }
            setStartDate(newStartDate);

            let newEndDate = { ...settings.find(s => s.organizationSetting === 'endDateUrbanPoints') || {} };
            if (dayjs(newEndDate.value).diff(dayjs(new Date())) > 0) {
                newEndDate.value = dayjs(new Date()).format("YYYY-MM-DD")//la data di fine non deve essere nel futuro
            }
            setEndDate(newEndDate);

            setSettingsLoaded(true);
        }
        setIncludeUsersWalletBalance(overrideInterval?.isAllInitiativePeriod ?? false);
    }, [settingsStatus, overrideInterval, settingsLoaded, settings, minLimitDate, isOdp]);

    const [intervalModal, setIntervalModal] = React.useState(false);
    const [enableLegsQuery, setEnableLegsQuery] = React.useState(false);
    const [touristicAnchorEl, setTouristicAnchorEl] = React.useState(null);

    const enableUgoLegs = React.useMemo(() => {
        const s = settings?.find(x => x.organizationSetting === "enableUgoLegs");
        // gestisci string/boolean
        return s?.value === true || s?.value === "true" || s?.value === 1 || s?.value === "1";
    }, [settings]);

    let [{
        status: sessionsStatus,
        data: sessionsStatistics
    }, {
        status: sessionsTypeStatus,
        data: sessionsTypesStatistics
    }, {
        status: sessionsVolumeByHourStatus,
        data: sessionsVolumeByHour
    }, {
        status: sessionsVolumeByDayStatus,
        data: sessionsVolumeByDay
    }, {
        status: usersVolumeByAgeStatus,
        data: usersVolumeByAge
    }, {
        status: usersStatisticsStatus,
        data: usersStatistics
    }, {
        status: legStatisticsStatus,
        data: legStatistics
    }, {
        status: cyclePathStatisticsStatus,
        data: cyclePathStatistics
    }, {
        status: wrongWayStatisticsStatus,
        data: wrongWayStatistics
    }, {
        status: outsideUrbanStatisticsStatus,
        data: outsideUrbanStatistics
    }] = useGetStatistics(
        oid,
        stringToUnix(overrideInterval ? overrideInterval.start : startDate.value),
        overrideInterval?.end || endDate?.value
            ? dayjs
                .utc(
                    overrideInterval ? overrideInterval.end : endDate.value,
                    "YYYY-MM-DD"
                )
                .add(1, "day")
                .valueOf()
            : null,
        includeUsersWalletBalance,
        enableLegsQuery,
        isAll
    );
    let [
        { status: sessionsStatusAll, data: sessionsStatisticsAll },
        { status: sessionsTypeStatusAll, data: sessionsTypesStatisticsAll },
        { status: sessionsVolumeByHourStatusAll, data: sessionsVolumeByHourAll },
        { status: sessionsVolumeByDayStatusAll, data: sessionsVolumeByDayAll },
        { status: usersVolumeByAgeStatusAll, data: usersVolumeByAgeAll },
        { status: usersStatisticsStatusAll, data: usersStatisticsAll },
        { status: legStatisticsStatusAll, data: legStatisticsAll },
        { status: cyclePathStatisticsStatusAll, data: cyclePathStatisticsAll },
        { status: wrongWayStatisticsStatusAll, data: wrongWayStatisticsAll },
        { status: outsideUrbanStatisticsStatusAll, data: outsideUrbanStatisticsAll },
    ] = useGetStatisticsForAll(
        stringToUnix(overrideInterval ? overrideInterval.start : startDate.value),
        overrideInterval?.end || endDate?.value
            ? dayjs
                .utc(
                    overrideInterval ? overrideInterval.end : endDate.value,
                    "YYYY-MM-DD"
                )
                .add(1, "day")
                .valueOf()
            : null,
        enableLegsQuery,
        !isAll
    );

    if (isAll && sessionsStatusAll) {
        sessionsStatus = sessionsStatusAll;
        sessionsStatistics = sessionsStatisticsAll;
        sessionsTypeStatus = sessionsTypeStatusAll;
        sessionsTypesStatistics = sessionsTypesStatisticsAll;
        sessionsVolumeByHourStatus = sessionsVolumeByHourStatusAll;
        sessionsVolumeByHour = sessionsVolumeByHourAll;
        sessionsVolumeByDayStatus = sessionsVolumeByDayStatusAll;
        sessionsVolumeByDay = sessionsVolumeByDayAll;
        usersVolumeByAgeStatus = usersVolumeByAgeStatusAll;
        usersVolumeByAge = usersVolumeByAgeAll;
        usersStatisticsStatus = usersStatisticsStatusAll;
        usersStatistics = usersStatisticsAll;
        legStatisticsStatus = legStatisticsStatusAll;
        legStatistics = legStatisticsAll;
        cyclePathStatistics = cyclePathStatisticsAll;
        cyclePathStatisticsStatus = cyclePathStatisticsStatusAll;
        wrongWayStatistics = wrongWayStatisticsAll;
        wrongWayStatisticsStatus = wrongWayStatisticsStatusAll;
        outsideUrbanStatistics = outsideUrbanStatisticsAll;
        outsideUrbanStatisticsStatus = outsideUrbanStatisticsStatusAll;
    }

    usersStatistics = usersStatistics ? usersStatistics[0] : {};
    sessionsStatistics = sessionsStatistics ?? {};
    let totUsers = usersStatistics.unknown + usersStatistics.male + usersStatistics.female;

    const replaceCurrencyFromLabel = React.useCallback((label) => {

        if (sessionsStatistics && sessionsStatistics.currency) {
            let currencyLabel = currency.find(c => sessionsStatistics.currency == c.code).label;
            return label.replaceAll('Euro', currencyLabel);
        }

        return label;
    }, [sessionsStatistics]);
    
    const topCards = [
        {
            title: t('users'),
            value: totUsers
        },
        {
            title: t('sessions'),
            value: sessionsStatistics.sessions,
            infoIcon:
                <Tooltip classes={{tooltip: styles.tooltip}} title={t('sessionCountInfo')}>
                    <IconButton sx={{color: 'info'}}>
                        <InfoIcon fontSize='small'/>
                    </IconButton>
                </Tooltip>,
        }, {
            title: replaceCurrencyFromLabel(t('totalEuro')),
            value: sessionsStatistics.totalEuros,
            unit: sessionsStatistics.currency,
            precision: 2,
            infoIcon:
                <Tooltip
                    classes={{ tooltip: styles.tooltip }}
                    title={
                        includeUsersWalletBalance
                            ? t('voucherAndWalletBalance')
                            : t('voucherValueOnly')
                    }>
                    <IconButton sx={{ color: 'info' }}>
                        <InfoIcon fontSize='small' />
                    </IconButton>
                </Tooltip>
        }, {
            title: t('totalKm'),
            value: sessionsStatistics.totalKm,
            unit: 'Km',
            precision: 1
        }, {
            title: t('totalCo2'),
            value: sessionsStatistics.totalCo2,
            unit: 'Kg',
            precision: 1,
            infoIcon:
                <Tooltip
                    classes={{ tooltip: styles.tooltip }}
                    title={t('co2CalculationInfo')}>
                    <IconButton sx={{ color: 'info' }}>
                        <InfoIcon fontSize='small' />
                    </IconButton>
                </Tooltip>,
        }, 
        (
            isTouristicOrganization || sessionsStatistics?.totalTouristicEuros != null
        ) && {
            title: replaceCurrencyFromLabel(t(isTouristicOrganization
                ? 'totalTouristicEuroDistributed'
                : 'totalTouristicEuro')
            ),
            value: sessionsStatistics.totalTouristicEuros,
            unit: sessionsStatistics.currency,
            precision: 2,
            valueEndElement: sessionsStatistics.touristicEuroDetails?.length > 0 && (
                <>
                    <IconButton
                        size="small"
                        onClick={(e) => setTouristicAnchorEl(touristicAnchorEl ? null : e.currentTarget)}
                    >
                        {touristicAnchorEl
                            ? <ExpandLessIcon fontSize="small" />
                            : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                    <Popover
                        open={Boolean(touristicAnchorEl)}
                        anchorEl={touristicAnchorEl}
                        onClose={() => setTouristicAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <Box sx={{ p: 2, minWidth: 280, maxWidth: 420 }}>
                            {sessionsStatistics.touristicEuroDetails.map((d) => (
                                <Box
                                    key={d.organizationTitle}
                                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, py: 0.5 }}
                                >
                                    <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                                        {d.organizationTitle}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                                        {numberWithSpaces(Number(d.sum).toFixed(2))} {sessionsStatistics.currency ?? ''}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Popover>
                </>
            )
        }
    ].filter(Boolean);

    const avgKmSectionCards = [
        {
            title: t('avgKmSessions'),
            value: sessionsStatistics.avgKm,
            unit: 'Km',
            precision: 2
        }, {
            title: t('avgKmHomeWork'),
            value: sessionsStatistics.avgKmHomeWork,
            unit: 'Km',
            precision: 2
        }, {
            title: t('avgKm'),
            value: sessionsStatistics.avgKmNoHomeWork,
            unit: 'Km',
            precision: 2
        },
    ];

    const cyclePathSectionCards = [
        {
            title: t('cyclePathPercentage'),
            value: (() => {
                const percentage = (cyclePathStatistics?.sessionStatistics.totalCyclePathKm / sessionsStatistics?.totalKm) * 100;
                return Number.isNaN(percentage) ? Number(0) : percentage
            })(),
            unit: '%',
            precision: 2,
            infoIcon:
                <Tooltip
                    classes={{ tooltip: styles.tooltip }}
                    title={t('cyclePathPercentageSessionStatsInfo')}>
                    <IconButton sx={{ color: 'info' }}>
                        <InfoIcon fontSize='small' />
                    </IconButton>
                </Tooltip>,
        }, {
            title: t('cyclePathTotalKm'),
            value: cyclePathStatistics?.sessionStatistics.totalCyclePathKm,
            unit: 'Km',
            precision: 2
        }, {
            title: t('cyclePathAvgKm'),
            value: cyclePathStatistics?.sessionStatistics.avgCyclePathKm,
            unit: 'Km',
            precision: 2
        }, 
    ];

    const wrongWaySectionCards = [
        {
            title: t('wrongWayPercentage'),
            value: (() => {
                const percentage = (wrongWayStatistics?.sessionStatistics.totalWrongWayKm / sessionsStatistics?.totalKm) * 100;
                return Number.isNaN(percentage) ? Number(0) : percentage
            })(),
            unit: '%',
            precision: 2,
        }, {
            title: t('wrongWayTotalKm'),
            value: wrongWayStatistics?.sessionStatistics.totalWrongWayKm,
            unit: 'Km',
            precision: 2
        }, {
            title: t('wrongWayAvgKm'),
            value: wrongWayStatistics?.sessionStatistics.avgWrongWayKm,
            unit: 'Km',
            precision: 2
        },
    ];

    const outsideUrbanSectionCards = [
        {
            title: t('outsideUrbanPercentage'),
            value: (() => {
                const percentage = (outsideUrbanStatistics?.sessionStatistics.totalOutsideUrbanKm / sessionsStatistics?.totalKm) * 100;
                return Number.isNaN(percentage) ? Number(0) : percentage
            })(),
            unit: '%',
            precision: 2,
        }, {
            title: t('outsideUrbanTotalKm'),
            value: outsideUrbanStatistics?.sessionStatistics.totalOutsideUrbanKm,
            unit: 'Km',
            precision: 2
        }, {
            title: t('outsideUrbanAvgKm'),
            value: outsideUrbanStatistics?.sessionStatistics.avgOutsideUrbanKm,
            unit: 'Km',
            precision: 2
        },
    ];

    let totalKmCards = React.useMemo(() => {
        let totalKmCards = [];
        //---- Km per leg -----
        Object.values(LEG_TYPE).filter(t => t !== LEG_TYPE.NONE).filter(t => enableUgoLegs || (t !== LEG_TYPE.UGO && t !== LEG_TYPE.PEDAL)).forEach((type) => {
            totalKmCards.push({
                title: `${legTypeTranslation[type]}`,
                value: legStatistics && legStatistics.find(l => l.legType === type)?.totalKm,
                unit: 'Km',
                precision: 1
            })
        });
        return totalKmCards;
    }, [enableUgoLegs, legStatistics, legTypeTranslation]);

    let avgKmCards = React.useMemo(() => {
        let avgKmCards = [];
        //---- Avg Km per leg -----
        Object.values(LEG_TYPE).filter(t => t !== LEG_TYPE.NONE).filter(t => enableUgoLegs || (t !== LEG_TYPE.UGO && t !== LEG_TYPE.PEDAL)).forEach((type) => {
            avgKmCards.push({
                title: `${legTypeTranslation[type]}`,
                value: legStatistics && legStatistics.find(l => l.legType === type)?.avgKm,
                unit: 'Km',
                precision: 1
            })
        });
        return avgKmCards;
    }, [enableUgoLegs, legStatistics, legTypeTranslation]);


    //wrap in use memo
    let co2Cards = React.useMemo(() => {
        let co2Cards = [];
        //---- Co2 per leg -----
        Object.values(LEG_TYPE).filter(t => t !== LEG_TYPE.NONE).filter(t => enableUgoLegs || (t !== LEG_TYPE.UGO && t !== LEG_TYPE.PEDAL)).forEach((type) => {
            co2Cards.push({
                title: `${legTypeTranslation[type]}`,
                value: legStatistics && legStatistics.find(l => l.legType === type)?.totalCo2,
                unit: 'Kg',
                precision: 1
            })
        });
        return co2Cards;
    }, [enableUgoLegs, legStatistics, legTypeTranslation]);

    let LegsCards = [
        {
            title: t('totalKm'),
            cards: totalKmCards
        }, {
            title: t('avgKmLeg'),
            cards: avgKmCards
        }, {
            title: t('totalCo2'),
            cards: co2Cards
        }
    ];

    const setDates = (interval) => {
        setStartDate({ ...startDate, value: interval.start });
        setEndDate({ ...endDate, value: interval.end });
    }

    //format di interval per semplificare
    let interval = {
        start: dayjs(new Date()).valueOf(),
        end: dayjs(new Date()).valueOf()
    }
    interval.start = startDate ? startDate.value : interval.start;
    interval.end = endDate ? endDate.value : interval.end;

    return (
        <Grid container direction={'column'} sx={(t) => ({ maxWidth: t.breakpoints.values.md, margin: 'auto' })}
            rowGap={3}>
            {!disableDateInterval &&
                <Grid item>
                    <Box sx={{display: 'flex', justifyContent: {xs: 'center', sm:'normal'}, alignItems: 'center', flexWrap: 'wrap'}}>
                        <StartIconButton
                            onClick={() => setIntervalModal(true)}
                            title={
                                settingsStatus === 'loading' ?
                                    <ResponsiveCircularLoading width={'1.5em'}/>
                                    :
                                    <Typography variant="subtitle1" component="div"
                                                style={{fontWeight: 600, fontSize: '1rem' }}>
                                        {dayjs(interval.start).format("DD/MM/YYYY") + " - " + dayjs(interval.end).format("DD/MM/YYYY")}
                                    </Typography>
                            }
                            startIcon={<DateRangeIcon/>}
                        />

                        <StartIconButton
                            title={
                                settingsStatus === 'loading' ?
                                    <ResponsiveCircularLoading width={'1.5em'}/>
                                    :
                                    <Typography variant="subtitle1" component="div"
                                                sx={{fontWeight: 600, fontSize: { xs:'0.88rem', sm: '1rem'}}}>
                                        {t('includeUserWallets')}
                                    </Typography>
                            }
                            onClick={() => setIncludeUsersWalletBalance((prev => !prev))}
                            startIcon={
                                <Radio
                                    value={includeUsersWalletBalance}
                                    checked={includeUsersWalletBalance}
                                sx={{padding: 0, margin: 0,
                                    '& svg': { fontSize: '20px' ,color:'primary.main'},
                                    }}
                                />}
                        />
                    </Box>
                    <DateIntervalModal
                        interval={interval}
                        onClose={() => setIntervalModal(false)}
                        open={intervalModal}
                        onSubmit={setDates}
                        forceLabelOnTop={true}/>
                </Grid>
            }
            <Grid item>
                <Grid container alignItems={"stretch"} justifyContent={"flex-start"}>
                    {
                        topCards.map((c, k) =>
                            <Grid item lg={4} sm={6} xs={12} key={k} sx={{ p: 2 }}>
                                <StatsCard title={c.title} value={c.value} unit={c.unit} precision={c.precision}
                                    multiplier={c.multiplier}
                                    loading={sessionsStatus === 'loading'}
                                    titleEndIcon={c.infoIcon}
                                    valueEndElement={c.valueEndElement}
                                >
                                    {c.children}
                                </StatsCard>
                            </Grid>
                        )
                    }
                </Grid>
            </Grid>
            <Grid item>
                <Grid container alignItems={"stretch"} justifyContent={"space-between"}>
                    {
                        avgKmSectionCards.map((c, k) =>
                            <Grid item lg={4} sm={6} xs={12} key={k} sx={{ p: 2 }}>
                                <StatsCard title={c.title} value={c.value} unit={c.unit} precision={c.precision}
                                    loading={sessionsStatus === 'loading'}
                                    titleEndIcon={c.infoIcon}
                                />
                            </Grid>
                        )
                    }
                </Grid>
            </Grid>
            <Grid item>
                <Grid container alignItems={"stretch"} justifyContent={"space-between"}>
                    {
                        cyclePathSectionCards.map((c, k) =>
                            <Grid item lg={4} sm={6} xs={12} key={k} sx={{ p: 2, minHeight: 120 }}>
                                <StatsCard title={c.title} value={c.value} unit={c.unit} precision={c.precision}
                                    loading={sessionsStatus === 'loading' || cyclePathStatisticsStatus === 'loading'}
                                    titleEndIcon={c.infoIcon}
                                />
                            </Grid>
                        )
                    }
                </Grid>
            </Grid>
            <Grid item>
                <Grid container alignItems={"stretch"} justifyContent={"space-between"}>
                    {
                        wrongWaySectionCards.map((c, k) =>
                            <Grid item lg={4} sm={6} xs={12} key={k} sx={{ p: 2, minHeight: 120 }}>
                                <StatsCard title={c.title} value={c.value} unit={c.unit} precision={c.precision}
                                    loading={sessionsStatus === 'loading' || wrongWayStatisticsStatus === 'loading'}
                                    titleEndIcon={c.infoIcon}
                                />
                            </Grid>
                        )
                    }
                </Grid>
            </Grid>
            <Grid item>
                <Grid container alignItems={"stretch"} justifyContent={"space-between"}>
                    {
                        outsideUrbanSectionCards.map((c, k) =>
                            <Grid item lg={4} sm={6} xs={12} key={k} sx={{ p: 2, minHeight: 120 }}>
                                <StatsCard title={c.title} value={c.value} unit={c.unit} precision={c.precision}
                                    loading={sessionsStatus === 'loading' || outsideUrbanStatisticsStatus === 'loading'}
                                    titleEndIcon={c.infoIcon}
                                />
                            </Grid>
                        )
                    }
                </Grid>
            </Grid>
            <Grid item>
                <Grid container gap={2}>
                    {
                        LegsCards.map(({ title, cards }) => (
                            <Grid item xs={12} key={title}>
                                <Pill lg={12} open={true} expandIconColor="secondary"
                                    onExpandedChange={(expanded) => setEnableLegsQuery(expanded)}
                                    pill={{
                                        id: 0,
                                        title: title,
                                        component: <Grid container alignItems={"stretch"} justifyContent={"space-between"}>
                                            {
                                                cards.map((c, k) =>
                                                    <Grid item lg={4} sm={6} xs={12} key={k} sx={{ p: 2 }} {...c.placeholderProps}>
                                                        {
                                                            !c.placeholder &&
                                                            <StatsCard title={c.title} value={c.value} unit={c.unit} precision={c.precision}
                                                                loading={legStatisticsStatus === 'loading'}
                                                                multiplier={c.multiplier} />
                                                        }
                                                    </Grid>
                                                )
                                            }
                                        </Grid>
                                    }} index={0} />
                            </Grid>
                        ))
                    }
                </Grid>
            </Grid>
            <Grid item sx={{ p: 2, px: 4 }}>
                <NTMLinearBar
                    data={sessionsStatistics &&
                    {
                        leisure: sessionsStatistics.leisure,
                        commute: sessionsStatistics.sessions - sessionsStatistics.leisure,
                    }
                    }
                    loading={sessionsStatus === 'loading'}
                    labels={[t('leisure'), t('commute')]}
                />
            </Grid>
            <Grid item sx={{ p: 2, px: 4 }}>
                <NTMLinearBar
                    data={sessionsTypesStatistics &&
                    {
                        bike: ((sessionsTypesStatistics.find(s => s.type === 0) || {}).sessions ?? 0),
                        commute: ((sessionsTypesStatistics.find(s => s.type === 1) || {}).sessions ?? 0),
                    }
                    }
                    loading={sessionsTypeStatus === 'loading'}
                    labels={[t('muscleBike'), t('eBike')]}
                />
            </Grid>
            <Grid item sx={{ p: 2, px: 4 }}>
                <NTMBubbleChart
                    data={sessionsVolumeByHour && ([...Array(24).keys()]).map(
                        h => ({
                            field: h,
                            value: (sessionsVolumeByHour.find(s => s.hour === h) || {}).sessions
                        }))}
                    loading={sessionsVolumeByHourStatus === 'loading'}
                    label={t('volumesByHour')}
                />
            </Grid>
            <Grid item sx={{ p: 2, px: 4 }}>
                <NTMBubbleChart
                    data={sessionsVolumeByDay && dayjs.weekdays().map((day, k) => ({ // per giorni del mese [...Array(dayjs().daysInMonth()).keys()]
                        field: day,
                        value: (sessionsVolumeByDay.find(s => s.day === k) || {}).sessions
                    }))}
                    loading={sessionsVolumeByDayStatus === 'loading'}
                    label={t('volumesByDay')}
                />
            </Grid>
            <Grid item sx={{ my: 1 }}>
                <Typography align={'center'} variant={'h5'}>{t('usersStatistics')}</Typography>
            </Grid>
            <Grid item sx={{ p: 2, px: 4 }}>
                <NTMLinearBar
                    data={usersStatistics &&
                    {
                        male: usersStatistics.male,
                        female: usersStatistics.female,
                        unknown: usersStatistics.unknown
                    }
                    }
                    loading={usersStatisticsStatus === 'loading'}
                    labels={[t('male'), t('female'), t('notDefined')]}
                />
            </Grid>
            <Grid item sx={{ p: 2, px: 4 }}>
                <NTMBarChart
                    data={
                        usersVolumeByAge?.sort((a, b) => AGE_ORDER.indexOf(a.ageRange) - AGE_ORDER.indexOf(b.ageRange))
                            .map(u => ({
                                //si assicura che venga decodificata la stringa in HTML
                                field: new DOMParser().parseFromString(AGE_RANGES[u.ageRange], 'text/html').documentElement.textContent,
                                value: u.users
                            }))
                    }
                    loading={usersVolumeByAgeStatus === 'loading'}
                    label={t('usersByAgeRange')}
                />
            </Grid>
        </Grid>
    )
}
